<script>
import { mapGetters } from 'vuex';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

// ✅ Alertas e erros iguais ao fluxo de Contatos
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

// ✅ Dialog de criar contato (vamos montar global)
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    CreateNewContactDialog,
  },

  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { accountId } = useAccount();
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();
    const { t } = useI18n();

    // ref do dialog global
    const createContactDialogRef = ref(null);

    return {
      router,
      route,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
      t,
      createContactDialogRef,
    };
  },

  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },

  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();

    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );

    // ✅ Atalho global ALT + H (capture=true)
    window.addEventListener('keydown', this.handleGlobalKeydown, true);
  },

  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }

    window.removeEventListener('keydown', this.handleGlobalKeydown, true);
  },

  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },

    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};

      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;

      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },

    // -------------------------
    // Helpers (ref compat)
    // -------------------------
    getCreateContactDialog() {
      const r = this.createContactDialogRef;
      // pode vir como Ref (r.value) ou já como instance (r)
      return r?.value ?? r ?? null;
    },

    // =========================
    // 🔥 Atalho global (ALT + H)
    // =========================
    handleGlobalKeydown(e) {
      const isTyping =
        e.target?.tagName === 'INPUT' ||
        e.target?.tagName === 'TEXTAREA' ||
        e.target?.isContentEditable;

      if (isTyping) return;

      const isAltH =
        e.altKey && (e.code === 'KeyH' || String(e.key).toLowerCase() === 'h');

      if (isAltH) {
        e.preventDefault();
        this.openCreateContactDialog();
      }
    },

    async openCreateContactDialog() {
      const dlg = this.getCreateContactDialog();
      if (!dlg) return;

      // igual ao fluxo do ContactsHeaderWrapper
      await dlg?.contactsFormRef?.resetValidation?.();
      dlg?.dialogRef?.open?.();
    },

    // -------------------------
    // Create (funciona normal)
    // -------------------------
    navigateToCreatedContact(contactId) {
      const accountId =
        this.route?.params?.accountId || this.currentAccountId || null;
      if (!accountId || !contactId) return;

      const page = this.route?.query?.page ? String(this.route.query.page) : '1';

      this.router.push({
        path: `/app/accounts/${accountId}/contacts/${contactId}`,
        query: { page },
      });
    },

    async onCreateContactGlobal(contact) {
      try {
        // cria contato igual ao fluxo padrão
        const created = await this.$store.dispatch('contacts/create', contact);

        // fecha + reseta form
        const dlg = this.getCreateContactDialog();
        dlg?.onSuccess?.();

        useAlert(
          this.t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
        );

        const id = created?.id;
        this.navigateToCreatedContact(id);
      } catch (error) {
        const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';

        if (error instanceof DuplicateContactException) {
          if (error.data?.includes?.('email')) {
            useAlert(this.t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
            return;
          }
          if (error.data?.includes?.('phone_number')) {
            useAlert(this.t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
            return;
          }
        }

        if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
          return;
        }

        useAlert(this.t(`${i18nPrefix}.ERROR_MESSAGE`));
      }
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>

    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>

    <WootSnackbarBox />
    <NetworkNotification />

    <!-- ✅ Dialog global: abre em qualquer tela e salva normal -->
    <CreateNewContactDialog
      ref="createContactDialogRef"
      @create="onCreateContactGlobal"
    />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

</style>
